import React, {useState} from 'react'
import {Row, Block} from 'jsxstyle'
import {ReactComponent as CalendarIcon} from '../../../common/assets/calendar-icon.svg'
import {ReactComponent as UserIcon} from '../../../common/assets/user-icon.svg'
import {ReactComponent as EyeIcon} from '../../../common/assets/eye-icon.svg'
import {ReactComponent as CopyIcon} from '../../../common/assets/copy-icon.svg'
import {ReactComponent as StarIcon} from '../../../common/assets/star-icon/clear-star-icon.svg'
import {XpertKnowledgeDto} from '../../../services/XpertKnowledge/type'
import {formatDate} from '../../../common/utils/dateUtils'
import {Tooltip} from '@mui/material'
import QuickConfirmDialog from '../../../components/QuickConfirmDialog'
import {useXpertKnowledgeContext} from '../XpertKnowledgeContext'

interface XpertKnowledgeRowProps {
  xpertKnowledge: XpertKnowledgeDto & {isSelected?: boolean}
  onRowClick?: () => void
  onEditClick?: () => void
}

const XpertKnowledgeRow: React.FC<XpertKnowledgeRowProps> = ({xpertKnowledge, onRowClick, onEditClick}) => {
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const [deleteIsHovered, setDeleteIsHovered] = useState(false)
  const {deleteXpertKnowledge} = useXpertKnowledgeContext()
  const filteredTitle = xpertKnowledge.publishTutorial?.title?.replace(/'/g, '`') || 'N/A'
  const filteredEquipmentType = xpertKnowledge.publishTutorial?.equipmentType?.replace(/'/g, '`') || `No Equipment Found`

  return (
    <Row
      display="grid"
      gridTemplateColumns="1.5fr 1.10fr 1.3fr 0.4fr 0.2fr 0.2fr 0.2fr"
      columnGap="35px"
      alignItems="center"
      padding="12px 20px"
      borderRadius="8px"
      border="1px solid #DAE4F4"
      backgroundColor="#FFFFFF"
      box-shadow="0px 2px 4px 0px #0000001A;"
      hoverBackgroundColor="#F7FAFD"
      cursor="pointer"
      width="100%"
      minHeight="66px"
      boxSizing="border-box"
      props={{onClick: onRowClick}}
    >
      <Tooltip title={filteredTitle}>
        <Block fontSize="16px" fontWeight={500} lineHeight="21px" minWidth="0" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
          {filteredTitle}
        </Block>
      </Tooltip>

      <Tooltip title={filteredEquipmentType}>
        <Block
          color="#909090"
          fontSize="14px"
          fontWeight={300}
          textAlign="center"
          minWidth="0"
          overflow="hidden"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {filteredEquipmentType}
        </Block>
      </Tooltip>

      <Row alignItems="center" gap="5px" minWidth="0">
        <UserIcon width={16} height={16} color="#909090" style={{minWidth: 16, minHeight: 16}} />
        <Tooltip title={xpertKnowledge.creator?.fullName ?? 'No name found'}>
          <Block
            color="#100E11"
            fontSize="14px"
            fontWeight={300}
            minWidth="0"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            props={{'data-attr-masked': 'true'} as any}
          >
            {xpertKnowledge.creator?.fullName ?? 'No name found'}
          </Block>
        </Tooltip>
      </Row>

      <Row alignItems="center" gap="5px">
        <CalendarIcon width={16} height={16} color="#909090" style={{minWidth: 16, minHeight: 16}} />
        <Block color="#100E11" fontSize="14px" fontWeight={300}>
          {xpertKnowledge.createdAt ? formatDate(xpertKnowledge.createdAt) : ''}
        </Block>
      </Row>

      <Row
        alignItems="center"
        gap="5px"
        cursor="pointer"
        props={{
          onClick: (e) => {
            e.stopPropagation()
            navigator.clipboard.writeText(xpertKnowledge.reference)
            alert('XpertKnowledge reference copied to clipboard')
          },
        }}
      >
        <Block color="#100E11" fontSize="14px" fontWeight={300}>
          ID
        </Block>
        <CopyIcon width={16} height={16} color="#0E78F5" />
      </Row>

      <Row alignItems="center" gap="5px">
        <EyeIcon width={16} height={16} color="#909090" />
        <Block color="#100E11" fontSize="14px" fontWeight={300}>
          {xpertKnowledge.publishTutorial?.views ?? 0}
        </Block>
      </Row>

      <Row alignItems="center" gap="5px">
        <Block color="#100E11" fontSize="14px" fontWeight={300}>
          {xpertKnowledge.publishTutorial?.avgRating ? xpertKnowledge.publishTutorial.avgRating.toFixed(2) : '0.00'}
        </Block>
        <Block>
          <StarIcon height={16} width={16} color="#909090" />
        </Block>
      </Row>

      {/* <Row gap="8px">
        {xpertKnowledge.isMine ? (
          <Row
            alignItems="center"
            gap="8px"
            backgroundColor="transparent"
            borderRadius="4px"
            border="1px solid #F1164A"
            hoverBackgroundColor="#F1164A"
            padding="8px 16px"
            cursor="pointer"
            justifyContent="center"
            maxHeight="18px"
            props={{
              onClick: (e) => {
                e.stopPropagation()
                setOpenDeleteConfirm(true)
              },

              onMouseEnter: () => setDeleteIsHovered(true),
              onMouseLeave: () => setDeleteIsHovered(false),
            }}
          >
            <TrashIcon height={20} width={20} color={deleteIsHovered ? '#FFFFFF' : '#EF2D2D'} />
          </Row>
        ) : (
          <Block minWidth="54px" />
        )}
        {/* <Tooltip title="Rating and comments" arrow>
          <Row
            alignItems="center"
            gap="8px"
            backgroundColor="#0E78F5"
            hoverBackgroundColor="#0053BA"
            borderRadius="4px"
            padding="8px 16px"
            cursor="pointer"
            justifyContent="center"
            props={{
              onClick: (e) => {
                e.stopPropagation()
                onEditClick?.()
              },
            }}
          >
            <ChatIcon height={20} width={20} color="#FFFFFF" />
          </Row>
        </Tooltip>
      </Row> */}
      <QuickConfirmDialog
        key={xpertKnowledge.id}
        open={openDeleteConfirm}
        setOpen={() => {
          setOpenDeleteConfirm(false)
        }}
        title="XpertKnowledge Confirm Deletion"
        content={'Are you sure you want to delete this?\nThis action cannot be reversed!'}
        buttons={[
          {
            title: 'Delete',
            onClick: () => {
              deleteXpertKnowledge(xpertKnowledge.id)
            },
            closeOnClick: true,
            customButtonProps: {
              backgroundColor: '#F1164A',
              hoverBackgroundColor: '#C41E2C',
              color: '#FFFFFF',
            },
          },
        ]}
      />
    </Row>
  )
}

export default XpertKnowledgeRow
